import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAlert } from '../../contexts/AlertContext';
import { useApi, ProfileFieldValue } from '../../contexts/ApiContext';
import Button from '../ui/Button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const humanizeId = (id: string): string => {
    if (/^[0-9a-f]{8}-/.test(id)) return id;
    return id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const parseOptions = (options?: string[] | string): string[] => {
    if (!options) return [];
    if (Array.isArray(options)) return options;
    try {
        const parsed = JSON.parse(options);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const CompanyForm: React.FC = () => {
    const [companyName, setCompanyName] = useState('');
    const [primaryContactName, setPrimaryContactName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [showGoBackButton, setShowGoBackButton] = useState(false);
    const [loadingCompany, setLoadingCompany] = useState(true);
    const [phoneNumberError, setPhoneNumberError] = useState('');

    // Profile field state
    const [profileFields, setProfileFields] = useState<ProfileFieldValue[]>([]);
    const [profileTextValues, setProfileTextValues] = useState<{ [id: string]: string }>({});
    const [profileBoolValues, setProfileBoolValues] = useState<{ [id: string]: boolean }>({});
    const [profileDateValues, setProfileDateValues] = useState<{ [id: string]: Date | null }>({});
    const [profileMultiValues, setProfileMultiValues] = useState<{ [id: string]: string[] }>({});
    const [profileErrors, setProfileErrors] = useState<{ [id: string]: string }>({});

    const { showAlert } = useAlert();
    const { fetchCompany, saveCompany } = useApi();
    const navigate = useNavigate();
    const location = useLocation();
    const fromTokenValidation = (location.state as { from?: string } | null)?.from === 'tokenValidation';
    const backButtonLabel = fromTokenValidation ? 'Continue →' : '← Go Back';
    const backButtonDestination = fromTokenValidation ? '/dashboard' : '/company-menu';

    useEffect(() => {
        const loadCompanyData = async () => {
            const companyData = await fetchCompany();

            if (companyData) {
                setCompanyName(companyData.company_name || '');
                setPrimaryContactName(companyData.primary_contact || '');
                setEmail(companyData.email || '');
                setPhoneNumber(companyData.phone_number || '');

                if (companyData.company_name && companyData.primary_contact && companyData.email) {
                    setShowGoBackButton(true);
                }

                // Load profile fields
                const rawFields = companyData.profile_field_values;
                const fields: ProfileFieldValue[] = (Array.isArray(rawFields) ? rawFields : []) as ProfileFieldValue[];
                const sorted = [...fields].sort((a, b) => Number(a.order) - Number(b.order));
                setProfileFields(sorted);

                const texts: { [id: string]: string } = {};
                const bools: { [id: string]: boolean } = {};
                const dates: { [id: string]: Date | null } = {};
                const multis: { [id: string]: string[] } = {};

                sorted.forEach(field => {
                    if (!field.value) return;
                    switch (field.format) {
                        case 'yes_no':
                            bools[field.profile_field_id] = field.value === 'true' || field.value === 'yes';
                            break;
                        case 'date': {
                            const parts = field.value.split('-');
                            if (parts.length === 3) {
                                dates[field.profile_field_id] = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                            }
                            break;
                        }
                        case 'dropdown_multi':
                            try {
                                const parsed = JSON.parse(field.value);
                                multis[field.profile_field_id] = Array.isArray(parsed) ? parsed : [field.value];
                            } catch {
                                multis[field.profile_field_id] = field.value ? field.value.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                            }
                            break;
                        default:
                            texts[field.profile_field_id] = field.value;
                    }
                });

                setProfileTextValues(texts);
                setProfileBoolValues(bools);
                setProfileDateValues(dates);
                setProfileMultiValues(multis);
            }

            setLoadingCompany(false);
        };

        loadCompanyData();
    }, []);

    const validateABN = (abn: string): boolean => {
        const abnDigits = abn.replace(/\s/g, '');
        return /^\d{11}$/.test(abnDigits);
    };

    // Restrict input to digits (spaces optional), capped at 11 actual digits
    const capAbnInput = (value: string): string => {
        let digitCount = 0;
        return value.replace(/[^0-9\s]/g, '').replace(/./g, (ch) => {
            if (/\d/.test(ch)) {
                if (digitCount >= 11) return '';
                digitCount++;
            }
            return ch;
        });
    };

    // Keep only digits and a single decimal point for number fields
    const sanitizeNumberInput = (value: string): string => {
        let cleaned = value.replace(/[^0-9.]/g, '');
        const firstDot = cleaned.indexOf('.');
        if (firstDot !== -1) {
            cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
        }
        return cleaned;
    };

    const validatePhoneNumber = (phone: string): boolean => {
        if (!phone) return true;
        const normalised = phone.replace(/[\s\-().]/g, '');
        const mobileNational = /^0[45][0-9]{8}$/;
        const mobileInternational = /^\+61[45][0-9]{8}$/;
        const landlineNational = /^0[2378][0-9]{8}$/;
        const landlineInternational = /^\+61[2378][0-9]{8}$/;
        const nonGeographic = /^1[38]00[0-9]{6}$/;
        return (
            mobileNational.test(normalised) ||
            mobileInternational.test(normalised) ||
            landlineNational.test(normalised) ||
            landlineInternational.test(normalised) ||
            nonGeographic.test(normalised)
        );
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleProfileMultiChange = (fieldId: string, option: string, checked: boolean) => {
        setProfileMultiValues(prev => {
            const current = prev[fieldId] || [];
            const updated = checked ? [...current, option] : current.filter(v => v !== option);
            return { ...prev, [fieldId]: updated };
        });
        clearProfileError(fieldId);
    };

    const handleProfileDateChange = (fieldId: string, date: Date | null) => {
        setProfileDateValues(prev => ({ ...prev, [fieldId]: date }));
    };

    const validateProfileFields = (): boolean => {
        const errors: { [id: string]: string } = {};

        for (const field of profileFields) {
            const id = field.profile_field_id;
            const label = field.label || humanizeId(id);

            // ABN profile field: validate format whenever a value is present
            if (label.trim().toUpperCase() === 'ABN') {
                const abnValue = profileTextValues[id]?.trim();
                if (abnValue && !validateABN(abnValue)) {
                    errors[id] = 'Please enter a valid 11-digit ABN';
                    continue;
                }
            }

            if (!field.required) continue;
            switch (field.format) {
                case 'text':
                    if (!profileTextValues[id]?.trim()) {
                        errors[id] = `Please fill in ${label}`;
                    }
                    break;
                case 'date':
                    if (!profileDateValues[id]) {
                        errors[id] = `Please enter a date for ${label}`;
                    }
                    break;
                case 'dropdown_multi':
                    if (!profileMultiValues[id]?.length) {
                        errors[id] = `Please select at least one option for ${label}`;
                    }
                    break;
            }
        }

        setProfileErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const clearProfileError = (id: string) => {
        setProfileErrors(prev => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const buildProfileFieldPayload = (): { [key: string]: string } => {
        const payload: { [key: string]: string } = {};
        profileFields.forEach(field => {
            const id = field.profile_field_id;
            switch (field.format) {
                case 'yes_no':
                    payload[id] = profileBoolValues[id] ? 'Yes' : 'No';
                    break;
                case 'date': {
                    const d = profileDateValues[id];
                    if (d) {
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        payload[id] = `${year}-${month}-${day}`;
                    }
                    break;
                }
                case 'dropdown_multi':
                    payload[id] = JSON.stringify(profileMultiValues[id] || []);
                    break;
                default:
                    payload[id] = profileTextValues[id] || '';
            }
        });
        return payload;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!companyName.trim()) {
            showAlert('Please enter a company name', 'error');
            return;
        }

        if (!primaryContactName.trim()) {
            showAlert('Please enter the primary contact name', 'error');
            return;
        }

        if (!email.trim() || !validateEmail(email)) {
            showAlert('Please enter a valid email address', 'error');
            return;
        }

        if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
            setPhoneNumberError('Please enter a valid phone number (e.g., 0412 345 678, 02 9123 4567, or +61 412 345 678)');
            return;
        }

        if (!validateProfileFields()) return;

        setLoading(true);

        const success = await saveCompany({
            company_name: companyName.trim(),
            primary_contact: primaryContactName.trim(),
            email: email.trim(),
            phone_number: phoneNumber.trim(),
            profile_field_values: buildProfileFieldPayload(),
        });

        setLoading(false);

        if (success) {
            setTimeout(() => navigate('/dashboard'), 1500);
        }
    };

    const handleGoBack = () => {
        navigate(backButtonDestination);
    };

    const renderProfileField = (field: ProfileFieldValue) => {
        const id = field.profile_field_id;
        const label = field.label || humanizeId(id);

        const errorEl = profileErrors[id] ? (
            <small style={{ color: '#f44336', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                {profileErrors[id]}
            </small>
        ) : null;

        switch (field.format) {
            case 'yes_no':
                return (
                    <div key={id} className="form-group">
                        <div className="checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    id={id}
                                    checked={profileBoolValues[id] || false}
                                    onChange={(e) => setProfileBoolValues(prev => ({ ...prev, [id]: e.target.checked }))}
                                    style={{ marginRight: '8px' }}
                                />
                                <span>
                                    {label}
                                    {field.required && <span style={{ color: 'red' }}> *</span>}
                                </span>
                            </label>
                        </div>
                        {errorEl}
                    </div>
                );

            case 'date':
                return (
                    <div key={id} className="form-group">
                        <label htmlFor={id}>
                            {label}
                            {field.required && <span style={{ color: 'red' }}> *</span>}
                        </label>
                        <DatePicker
                            id={id}
                            selected={profileDateValues[id] || null}
                            onChange={(date) => { handleProfileDateChange(id, date); clearProfileError(id); }}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            yearDropdownItemNumber={100}
                            scrollableYearDropdown
                            autoComplete="off"
                            className="date-picker-input"
                            wrapperClassName="date-picker-wrapper"
                        />
                        <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                            Format: dd/mm/yyyy
                        </small>
                        {errorEl}
                    </div>
                );

            case 'number': {
                const isAbn = label.trim().toUpperCase() === 'ABN';
                return (
                    <div key={id} className="form-group">
                        <label htmlFor={id}>
                            {label}
                            {field.required && <span style={{ color: 'red' }}> *</span>}
                        </label>
                        <input
                            type="text"
                            inputMode={isAbn ? 'numeric' : 'decimal'}
                            id={id}
                            value={profileTextValues[id] || ''}
                            onChange={(e) => {
                                const next = isAbn ? capAbnInput(e.target.value) : sanitizeNumberInput(e.target.value);
                                setProfileTextValues(prev => ({ ...prev, [id]: next }));
                                clearProfileError(id);
                            }}
                            placeholder={isAbn ? '12 345 678 901' : `Enter ${label.toLowerCase()}`}
                            maxLength={isAbn ? 14 : undefined}
                            required={field.required}
                        />
                        <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                            {isAbn ? '11 digits (spaces optional)' : 'Numbers only, e.g. 12 or 12.5 (decimals allowed)'}
                        </small>
                        {errorEl}
                    </div>
                );
            }

            case 'dropdown_single': {
                const options = parseOptions(field.options);
                return (
                    <div key={id} className="form-group">
                        <label htmlFor={id}>
                            {label}
                            {field.required && <span style={{ color: 'red' }}> *</span>}
                        </label>
                        <select
                            id={id}
                            value={profileTextValues[id] || ''}
                            onChange={(e) => { setProfileTextValues(prev => ({ ...prev, [id]: e.target.value })); clearProfileError(id); }}
                            required={field.required}
                        >
                            <option value="">-- Select an option --</option>
                            {options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                        {errorEl}
                    </div>
                );
            }

            case 'dropdown_multi': {
                const options = parseOptions(field.options);
                const selected = profileMultiValues[id] || [];
                return (
                    <div key={id} className="form-group">
                        <label>
                            {label}
                            {field.required && <span style={{ color: 'red' }}> *</span>}
                        </label>
                        <div
                            id={id}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                maxHeight: '132px',
                                overflowY: 'auto',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px 12px',
                            }}
                        >
                            {options.map((opt) => (
                                <label key={opt} className="checkbox-label" style={{ fontWeight: 'normal', minHeight: '36px', alignItems: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(opt)}
                                        onChange={(e) => handleProfileMultiChange(id, opt, e.target.checked)}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <span>{opt}</span>
                                </label>
                            ))}
                            {options.length === 0 && (
                                <small style={{ color: '#999' }}>No options available</small>
                            )}
                        </div>
                        {errorEl}
                    </div>
                );
            }

            default: { // text
                const isAbn = label.trim().toUpperCase() === 'ABN';
                return (
                    <div key={id} className="form-group">
                        <label htmlFor={id}>
                            {label}
                            {field.required && <span style={{ color: 'red' }}> *</span>}
                        </label>
                        <input
                            type="text"
                            id={id}
                            inputMode={isAbn ? 'numeric' : undefined}
                            value={profileTextValues[id] || ''}
                            onChange={(e) => {
                                const next = isAbn ? capAbnInput(e.target.value) : e.target.value;
                                setProfileTextValues(prev => ({ ...prev, [id]: next }));
                                clearProfileError(id);
                            }}
                            placeholder={isAbn ? '12 345 678 901' : `Enter ${label.toLowerCase()}`}
                            maxLength={isAbn ? 14 : undefined}
                            required={field.required}
                        />
                        {isAbn && (
                            <small style={{ color: '#666', fontSize: '0.85em' }}>
                                11 digits (spaces optional)
                            </small>
                        )}
                        {errorEl}
                    </div>
                );
            }
        }
    };

    if (loadingCompany) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                <p>Loading company information...</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-group">
                <label htmlFor="companyName">
                    Company Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="text"
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="primaryContactName">
                    Primary Contact Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="text"
                    id="primaryContactName"
                    value={primaryContactName}
                    onChange={(e) => setPrimaryContactName(e.target.value)}
                    placeholder="Enter primary contact name"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="email">
                    Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@company.com"
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="phoneNumber">
                    Phone Number
                </label>
                <input
                    type="tel"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        if (phoneNumberError) setPhoneNumberError('');
                    }}
                    placeholder="0412 345 678"
                    style={phoneNumberError ? { borderColor: '#f44336' } : {}}
                />
                {phoneNumberError && (
                    <small style={{ color: '#f44336', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                        {phoneNumberError}
                    </small>
                )}
                {!phoneNumberError && (
                    <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                        Format: 0412 345 678, 02 9123 4567, or +61 412 345 678 (optional)
                    </small>
                )}
            </div>

            {profileFields.map(renderProfileField)}

            <div className="button-group">
                <Button type="submit" variant="primary" loading={loading}>
                    Save Changes
                </Button>
                {showGoBackButton && (
                    <Button type="button" variant="secondary" onClick={handleGoBack}>
                        {backButtonLabel}
                    </Button>
                )}
            </div>
        </form>
    );
};

export default CompanyForm;
